'use client';

export type CallPresenceEvent =
  | { type: 'call:active'; callId: string; staffId: string; tabId: string }
  | { type: 'call:picked-up-elsewhere'; callId: string; byStaffId: string; byTabId: string }
  | { type: 'call:ended'; callId: string; tabId: string }
  | { type: 'call:heartbeat'; callId: string; staffId: string; tabId: string };

const CHANNEL_NAME = 'hotelstack-call-presence';
const HEARTBEAT_MS = 5000;
const STALE_MS = 12000;

type Handler = (event: CallPresenceEvent) => void;

class CallPresenceBus {
  private channel: BroadcastChannel | null = null;
  private handlers = new Set<Handler>();
  private tabId = Math.random().toString(36).slice(2, 10);
  private heartbeatRaf: number | null = null;
  private lastHeartbeatTs = 0;
  private activeCallId: string | null = null;
  private activeStaffId: string | null = null;
  private lastSeen = new Map<string, number>();

  connect(): void {
    if (typeof window === 'undefined') return;
    if (this.channel) return;

    this.channel = new BroadcastChannel(CHANNEL_NAME);
    this.channel.onmessage = (e: MessageEvent<CallPresenceEvent>) => {
      this.lastSeen.set(
        `${(e.data as CallPresenceEvent & { tabId?: string }).tabId}`,
        Date.now()
      );
      for (const handler of this.handlers) {
        handler(e.data);
      }
    };
  }

  disconnect(): void {
    this.stopHeartbeat();
    this.channel?.close();
    this.channel = null;
  }

  subscribe(handler: Handler): () => void {
    this.handlers.add(handler);
    return () => this.handlers.delete(handler);
  }

  publishActive(callId: string, staffId: string): void {
    this.activeCallId = callId;
    this.activeStaffId = staffId;
    this.broadcast({ type: 'call:active', callId, staffId, tabId: this.tabId });
    this.startHeartbeat();
  }

  publishEnded(callId: string): void {
    this.broadcast({ type: 'call:ended', callId, tabId: this.tabId });
    this.activeCallId = null;
    this.activeStaffId = null;
    this.stopHeartbeat();
  }

  publishPickedUp(callId: string, byStaffId: string): void {
    this.broadcast({
      type: 'call:picked-up-elsewhere',
      callId,
      byStaffId,
      byTabId: this.tabId,
    });
  }

  isActiveElsewhere(callId: string): boolean {
    for (const [tabId, ts] of this.lastSeen) {
      if (tabId !== this.tabId && Date.now() - ts < STALE_MS) {
        return true;
      }
    }
    return false;
  }

  getTabId(): string {
    return this.tabId;
  }

  private broadcast(event: CallPresenceEvent): void {
    this.channel?.postMessage(event);
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.lastHeartbeatTs = 0;
    const tick = (now: number) => {
      if (now - this.lastHeartbeatTs >= HEARTBEAT_MS) {
        this.lastHeartbeatTs = now;
        if (this.activeCallId && this.activeStaffId) {
          this.broadcast({
            type: 'call:heartbeat',
            callId: this.activeCallId,
            staffId: this.activeStaffId,
            tabId: this.tabId,
          });
        }
      }
      this.heartbeatRaf = requestAnimationFrame(tick);
    };
    this.heartbeatRaf = requestAnimationFrame(tick);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatRaf !== null) {
      cancelAnimationFrame(this.heartbeatRaf);
      this.heartbeatRaf = null;
    }
  }
}

export const callPresenceBus = new CallPresenceBus();
